'use strict';
/* ==========================================================================
   levels/zine.js — step "zine" (orientation any): FIX THE ZINE (DESIGN.md §5.3, look §2).

   Six zine pages per attempt from content/bank.zine.json through the model: Devices.attempt('zine', json) →
   Devices.drawZine (1 legit page + 5 pages with different cheats, easiest first; the seed is saved in
   Game.progress.bank.zine so a reload resumes the same pages). Each page is a photocopied "DEVICE OF THE WEEK" page
   taped to the corkboard with 3 examples, each with a hand-lettered sticker in its device's highlighter colour
   ('none' = NOT FIGURATIVE).
   Per page:  drop → spot: tap the WRONG sticker, or "All correct". A tap on a right sticker is a miss ("That label's
                     right: …"); after 3 misses Ms. Park circles it herself. Tapping an example's words shows its
                     sticker's definition (Devices.DEVICES: free, never a miss).
              → ask: cheat pages "What's the right label?" (the page's options as stickers, shuffled by the model); legit
                     pages ask their own `q`. A wrong pick is struck out with its `why`; pick again. Whether a pick is
                     right is decided by the model (Devices.check on the bad example), never by the content file.
              → fix: Ms. Park's highlighter peels the wrong sticker off and letters the right one in its colour, with the
                     page's `fix` as her note (legit pages: a tick on every sticker + ALL CORRECT) → next page.
   Stars: right sticker (or "All correct") first try = 1 + right pick first try = 1. of 12, pass = json.passStars (8).
   Game.progress.scores.zine = {best, last, of}; first answers under answers['zine/<id>/label'] and ['zine/<id>'];
   Devices.finish at the end; the stamp (Game.complete) through "Collect your stamp" (a pass that leaves keeps it).
   Resume: Game.progress.bank.zine.run = {i, ev, res}: the current page's taps / definitions / picks are replayed.

   Portrait phone (Dock.active): the stage shows the page large (a picture); the stickers are Dock.choices, "All correct"
   a dock button, the definitions dock buttons, the picks Dock.choices with the why as notes, the fix a list.
   Landscape phone: the stickers / words are canvas tap targets ≥ 44 CSS px (UI.tap), text through UI.text.
   Keys: 1–3 = sticker n, A = All correct, 1–3 = option, Enter = next / start.

   QA hooks: sc.phase ('intro' | 'drop' | 'spot' | 'ask' | 'fix' | 'end'), sc.page (the Devices.drawZine page),
   sc.answer() (the bad example index or 'legit'), sc.tapLabel(i), sc.legit(), sc.showDef(i), sc.choose(i), sc.next(),
   sc.begin(fresh), sc.collect(), sc.back(), sc.done(); sc.pages, sc.bi, sc.results, sc.stars, sc.seed, sc.attempt,
   sc.resumed, sc.misses, sc.picks, sc.marks, sc.defs; Levels.zine.solution ({legit, label, answer, choice, right}),
   Levels.zine.targets() (the live canvas tap rects: stickers + words). finishStub(stars) (tools/integration.js) records
   the score and stamps the step.
   ========================================================================== */
(function () {
  const STEP = 'zine', MAX_MISSES = 3, DROP = 0.7, NEXT_GUARD = 0.3;
  const Z = () => window.Zine;
  const json = () => (Game.content && Game.content.bank_zine) || { pool: STEP, draw: { n: 6 }, items: [] };
  const TYPE = "'Special Elite', 'Courier New', monospace", MARKER = "'Permanent Marker', 'Caveat Brush', cursive", HAND = "'Caveat Brush', cursive";
  const PARK = '#2b4a8c', RED = '#d9352c', GREY = '#6d6a64', INK = '#141210', YEL = '#fff04a';
  const SHORT = {
    legit: 'all correct', likeIsSimile: '"like" that compares nothing', metaphorAsSimile: 'a metaphor called a simile',
    simileAsMetaphor: 'a simile called a metaphor', soundSwap: 'the sound devices swapped', hyperboleAsLie: 'hyperbole called "not figurative"',
    animalAsPersonification: 'a real animal called personification', coincidenceAsIrony: 'a coincidence called irony',
    idiomAsLiteral: 'an idiom called "not figurative"',
  };
  const DEV = id => (window.Devices && Devices.DEVICES[id]) || { name: String(id), short: '', def: '' };
  const LABEL = id => (id === 'none' ? 'NOT FIGURATIVE' : Devices.name(id).toUpperCase());
  const prog = (t0, d) => Math.max(0, Math.min(1, (T - t0) / d));
  const fnt = (s, f) => `${Math.round(s)}px ${f}`;
  const tw = (s, f) => { g.save(); g.font = f; const w = g.measureText(s).width; g.restore(); return w; };
  const wrapF = (s, maxW, f) => UI._wrap(s, maxW, f);
  const q10 = v => Math.ceil(v / 10) * 10;

  /* ---------------- the hand-lettered sticker ---------------- */
  function stickerSize(id, size) {
    const t = LABEL(id);
    return { t, w: Math.round(tw(t, fnt(size, MARKER)) + size * 2.3), h: Math.round(size * 1.6) };
  }
  /* centre cx, cy; {rot, s, alpha, seed, faded} */
  function sticker(id, cx, cy, size, { rot = 0, s = 1, alpha = 1, seed = 1, faded = false } = {}) {
    if (s <= 0.001 || alpha <= 0) return;
    const S = stickerSize(id, size);
    g.save(); g.translate(cx, cy); g.rotate(rot); g.scale(s, s); g.globalAlpha *= alpha * (faded ? 0.55 : 1);
    paperRect(0, 0, S.w, S.h, id === 'none' ? '#ecebe4' : Devices.colour(id), { seed, depth: 0.8, boil: false });
    Z().doodle(id, -S.w / 2 + size * 0.85, 0, size * 0.4, { seed: seed + 3, w: Math.max(2, size * 0.09) });
    g.font = fnt(size, MARKER); g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillStyle = INK;
    g.fillText(S.t, -S.w / 2 + size * 1.6, size * 0.06);
    g.restore();
    return S;
  }
  /* an ink ellipse (the found sticker, circled) */
  function ellipse(cx, cy, rx, ry, o) {
    const pts = []; for (let i = 0; i <= 34; i++) { const a = -0.4 + i / 32 * TAU; pts.push([cx + Math.cos(a) * rx, cy + Math.sin(a) * ry]); }
    ink(pts, { passes: 1, step: 10, ...o });
  }
  function tick(x, y, s, o = {}) { ink([[x - 16 * s, y], [x - 4 * s, y + 13 * s], [x + 20 * s, y - 18 * s]], { w: 6 * s, color: o.color || '#2f8f3a', seed: o.seed || 3, prog: o.prog == null ? 1 : o.prog, passes: 1, step: 6 }); }

  /* ---------------- the page layout (mode 'm' mouse, 't' touch landscape, 'd' the docked picture) ---------------- */
  function layPage(pg, mode) {
    const d = mode === 'd', t = mode === 't';
    const X = d ? 960 : t ? 668 : 690, W = d ? 1840 : t ? 1276 : 1250;
    const top = d ? 16 : 118, bottom = d ? 1066 : 1062, H = bottom - top, Y = top + H / 2;
    const left = X - W / 2 + (d ? 130 : 108), right = X + W / 2 - (d ? 56 : 50), maxW = right - left;
    const headS = d ? 60 : t ? Math.max(52, UI.text(46)) : 50, subS = d ? 34 : Math.round(UI.text(26));
    const start = top + (d ? 150 : 148);
    let L;
    for (const k of [1, 0.92, 0.84, 0.76, 0.68, 0.6]) {
      const ts = Math.round((d ? 72 : UI.text(44)) * k), ls = Math.round((d ? 54 : UI.text(34)) * Math.max(k, 0.7)), lh = ts * 1.28;
      let y = start, total = 0;
      const ex = pg.examples.map((e, i) => {
        const lines = Z().wrap(e.text, maxW, ts), S = stickerSize(e.label, ls);
        const lastW = Z().measure(lines[lines.length - 1], ts);
        const inline = lastW + 50 + S.w <= maxW;
        const drop = inline && lines.length > 1 ? Math.max(0, (S.h - lh) / 2 + 6) : 0;      // a tall sticker clears the line above
        const textH = lines.length * lh, h = inline ? Math.max(textH + drop, S.h + 10) : textH + S.h + 14;
        total += h;
        return { i, e, lines, S, inline, textH, h, drop };
      });
      const avail = bottom - 40 - start;
      if (total + 2 * 24 > avail && k > 0.6) continue;
      const gap = Math.max(20, Math.min(d ? 110 : 150, (avail - total) / 3));
      y = start + Math.max(0, (avail - total - gap * 2) / 2) * 0.5;
      const rows = ex.map((x, j) => {
        const y0 = y; y += x.h + gap;
        const tl = x.lines.map((s, n) => ({ s, y: y0 + (n + 0.5) * lh }));
        const last = tl[tl.length - 1];
        const sy = x.inline ? (x.lines.length > 1 ? last.y + x.drop : y0 + x.h / 2) : y0 + x.textH + 8 + x.S.h / 2;
        if (x.inline && x.lines.length === 1) tl[0].y = sy;
        const sx = right - x.S.w / 2;
        const textW = Math.max(...x.lines.map(s => Z().measure(s, ts)));
        const hitH = Math.max(UI.tap(64), x.S.h + 8);
        return { i: x.i, e: x.e, lines: tl, ts, ls, lh, top: y0, h: x.h, cy: y0 + x.h / 2,
          st: { x: sx, y: sy, w: x.S.w, h: x.S.h, hw: x.S.w + 16, hh: hitH, rot: [-0.035, 0.028, -0.02][j] + (hash1(j * 7 + (pg.id || '').length) - 0.5) * 0.02 },
          words: { x: left + textW / 2, y: y0 + x.textH / 2, w: textW + 20, h: Math.max(x.textH, UI.tap(64)) } };
      });
      L = { mode, X, Y, W, H, top, bottom, left, right, headS, subS, rows, ts, panelX: t ? 1328 : 1345 };
      break;
    }
    return L;
  }

  class FixZine extends Scene {
    get n() { return this.pages ? this.pages.length : (json().draw || {}).n || 6; }
    get of() { return this.n * 2; }
    get passStars() { return Math.min(this.of, json().passStars || Math.ceil(this.of * 2 / 3)); }
    get page() { return this.R || null; }
    get stars() { return (this.results || []).reduce((a, r) => a + (r ? r.stars : 0), 0); }
    get solution() {
      const R = this.R; if (!R) return null;
      return { legit: R.legit, label: R.badIndex, answer: R.legit ? 'legit' : R.badIndex, choice: R.ask.answerIndex, right: R.ask.options[R.ask.answerIndex].id };
    }
    answer() { return this.R ? (this.R.legit ? 'legit' : this.R.badIndex) : null; }
    /* tools/integration.js (the stub era): a draw of n pages through the model */
    get preview() { return { pages: this.pages || Devices.drawZine(json(), { seed: Bank.seedOverride() != null ? Bank.seedOverride() : 1 }).pages }; }

    enter() {
      this.phase = 'intro'; this.pages = null; this.R = null; this.bi = 0; this.results = []; this.passed = false; this.collected = false;
      this.lays = new Map();
      Audio.play('paper', { gain: 0.5 });
      const st = Game.progress.bank && Game.progress.bank[STEP];
      if (st && st.open && st.run && st.pool === STEP) this.begin();              // an attempt in progress: straight back in
    }
    exit() { this.bankStamp(); }
    back() { Game.replace(new Levels.map()); }

    /* ---- the attempt ---- */
    begin(fresh = false) {
      this.bankStamp();
      const S = Game.progress.bank || {}, prev = S[STEP] && S[STEP].open ? S[STEP].run : null;
      const a = Devices.attempt(STEP, json(), { fresh });
      this.pages = a.pages; this.seed = a.seed; this.attempt = a.attempt; this.resumed = a.resumed;
      this.results = []; this.passed = false; this.collected = false;
      if (a.resumed && prev && this.restore(prev)) return;
      this.startPage(0);
    }
    restore(run) {
      try {
        if (!(run.i >= 0 && run.i < this.pages.length)) return false;
        this.results = (run.res || []).slice(0, run.i);
        this.setup(run.i);
        this.pageAt = Game.time - DROP - 1; this.phase = 'spot'; this.landed = true;
        this.quiet = true;
        for (const [k, v] of run.ev || []) { if (k === 't') this.tapLabel(v); else if (k === 'l') this.legit(); else if (k === 'c') this.choose(v); else if (k === 'd') this.showDef(v); }
        this.quiet = false;
        this.save();
        return true;
      } catch (e) { this.quiet = false; console.warn('[zine] could not resume: ' + e.message); return false; }
    }
    setup(i) {
      this.bi = i; this.R = this.pages[i]; this.lays.clear();
      this.ev = []; this.misses = 0; this.marks = {}; this.defs = {}; this.lastDef = -1; this.found = -1; this.revealed = false;
      this.picks = []; this.picked = null; this.say = null; this.spotStar = 0; this.landed = false;
      this.pageAt = Game.time; this.foundAt = this.askAt = this.fixAt = this.pickedAt = this.lastDefAt = null;
    }
    startPage(i) {
      this.setup(i); this.phase = 'drop';
      this.save();
      this.sfx('whoosh', { gain: 0.3 });
    }
    save() {
      const S = Game.progress.bank && Game.progress.bank[STEP];
      if (S && S.open && this.phase !== 'end') S.run = { i: this.bi, ev: this.ev.slice(), res: this.results.slice() };
      Game.progress.save();
    }
    sfx(name, o) { if (!this.quiet && Audio.has && Audio.has(name)) Audio.play(name, o); }
    sfxAny(names, o) { const n = names.find(x => Audio.has && Audio.has(x)); if (n) this.sfx(n, o); }

    /* ---- an example's words: its sticker's definition (free) ---- */
    showDef(i) {
      const R = this.R;
      if (!R || !(i >= 0 && i < R.examples.length) || !(this.phase === 'drop' || this.phase === 'spot')) return false;
      if (this.phase === 'drop') this.phase = 'spot';
      this.ev.push(['d', i]);
      this.defs[i] = this.defs[i] || { at: Game.time };
      this.lastDef = i; this.lastDefAt = Game.time;
      this.sfx('pop', { gain: 0.3, rate: 1.2 });
      this.save();
      return true;
    }
    defText(i) { const id = this.R.examples[i].label, D = DEV(id); return { head: LABEL(id), short: D.short, def: D.def }; }

    /* ---- spot the wrong sticker ---- */
    tapLabel(i) {
      const R = this.R;
      if (this.phase === 'drop') this.phase = 'spot';
      if (this.phase !== 'spot' || !R || !(i >= 0 && i < R.examples.length) || this.marks[i]) return false;
      this.ev.push(['t', i]);
      const ex = R.examples[i];
      Game.progress.answer(STEP + '/' + R.key + '/label', `${ex.text} [${LABEL(ex.label)}]`);   // first try only
      if (!R.legit && i === R.badIndex) { this.spotStar = this.misses === 0 ? 1 : 0; this.foundLabel(i, false); this.save(); return true; }
      this.marks[i] = { at: Game.time };
      this.miss(`That label's right. ${LABEL(ex.label)}: ${DEV(ex.label).short}`);
      this.save();
      return true;
    }
    legit() {
      const R = this.R;
      if (this.phase === 'drop') this.phase = 'spot';
      if (this.phase !== 'spot' || !R) return false;
      this.ev.push(['l']);
      Game.progress.answer(STEP + '/' + R.key + '/label', 'All correct');           // first try only
      if (R.legit) { this.spotStar = this.misses === 0 ? 1 : 0; this.toAsk(); this.sfxAny(['snaps', 'ding'], { gain: 0.6 }); this.save(); return true; }
      this.miss('Not quite: one sticker on this page is wrong. Read each one against its words.');
      this.save();
      return true;
    }
    miss(text) {
      this.misses++; this.say = { text, at: Game.time };
      this.sfx('thunk', { gain: 0.4, rate: 0.9 });
      if (this.misses >= MAX_MISSES) {
        const R = this.R;
        this.spotStar = 0;
        if (R.legit) { this.say = { text: 'Ms. Park: every sticker on this page is right.', at: Game.time }; this.revealed = true; this.toAsk(); }
        else { this.say = { text: 'Ms. Park circles the wrong one for you.', at: Game.time }; this.foundLabel(R.badIndex, true); }
      }
    }
    foundLabel(i, revealed) {
      this.found = i; this.revealed = revealed; this.foundAt = Game.time;
      if (!revealed) { this.say = null; this.sfxAny(['snaps', 'ding'], { gain: 0.6 }); }
      this.sfx('scribble', { gain: 0.35 });
      this.toAsk(0.6);
    }
    toAsk(delay = 0.4) { this.phase = 'ask'; this.askAt = Game.time + delay; }

    /* ---- the right label (or the legit page's question) ---- */
    isRight(i) {
      const R = this.R, o = R.ask.options[i];
      if (R.legit) return i === R.ask.answerIndex;
      const ex = R.examples[R.badIndex];
      return Devices.check(`${R.id}.${ex.i}`, o.id).ok;                              // the model decides
    }
    choose(i) {
      const R = this.R;
      if (this.phase !== 'ask' || !R || !(i >= 0 && i < R.ask.options.length) || this.picks.includes(i)) return false;
      this.ev.push(['c', i]);
      if (!this.picks.length) Game.progress.answer(STEP + '/' + R.key, R.ask.options[i].label);   // first try only
      this.picks.push(i); this.pickedAt = Game.time;
      if (this.isRight(i)) {
        this.picked = i;
        this.sfxAny(['snaps', 'ding'], { gain: 0.7 });
        this.toFix(this.spotStar, this.picks.length === 1 ? 1 : 0);
      } else this.sfx('thunk', { gain: 0.35, rate: 0.8 });
      this.save();
      return true;
    }
    toFix(spotStar, askStar) {
      const R = this.R;
      this.phase = 'fix'; this.fixAt = Game.time; this._fc = null;
      this.results[this.bi] = { id: R.id, key: R.key, cheat: R.cheat, legit: R.legit, misses: this.misses, revealed: this.revealed,
        wrongPicks: this.picks.length - 1, spotStar, askStar, stars: spotStar + askStar };
      this.sfx(R.legit ? 'scribble' : 'tear', { gain: 0.45 });
    }
    rightId() { const R = this.R; return R.legit ? null : R.ask.options[this.picked != null ? this.picked : R.ask.answerIndex].id; }
    next() {
      if (this.phase !== 'fix' || Game.time - this.fixAt < NEXT_GUARD) return false;
      if (this.bi < this.pages.length - 1) this.startPage(this.bi + 1);
      else this.finish();
      return true;
    }
    finish() {
      const s = this.stars;
      this.phase = 'end'; this.endAt = Game.time; this.passed = s >= this.passStars;
      const S = Game.progress.bank && Game.progress.bank[STEP]; if (S) delete S.run;
      Devices.finish(STEP);                                                          // the next attempt draws new pages
      const P_ = Game.progress, prev = P_.scores[STEP];
      P_.scores[STEP] = { best: Math.max(prev && prev.best || 0, s), last: s, of: this.of };
      P_.save();
      Audio.play(this.passed ? 'sparkle' : 'paper');
    }
    bankStamp() {
      if (this.passed && !this.collected) { this.collected = true; Game.progress.stamps[STEP] = true; Game.progress.save(); Game._justStamped = STEP; }
    }
    collect() { if (this.collected) return; this.collected = true; Game.complete(STEP); }
    done() {
      if (!this.pages) this.begin();
      if (this.phase !== 'end') this.finish();
      this.collected = true; Game.complete(STEP);
    }
    finishStub(stars = this.of) {
      if (this._stubbed) return; this._stubbed = true;
      if (this.pages && this.phase !== 'end') { const S = Game.progress.bank && Game.progress.bank[STEP]; if (S) delete S.run; Devices.finish(STEP); }
      const S = Game.progress.scores, prev = S[STEP] || {};
      S[STEP] = { best: Math.max(prev.best || 0, stars), last: stars, of: this.of };
      Game.progress.save();
      this.collected = true;
      Game.complete(STEP);
    }
    endButtons() {
      return this.passed
        ? [{ label: 'Collect your stamp', go: () => this.collect() }, { label: 'Try again (new pages)', go: () => this.begin() }]
        : [{ label: 'Try again (new pages)', go: () => this.begin() }, { label: 'Back to the map', go: () => this.back() }];
    }

    update() {
      if (this.phase === 'drop' && Game.time - this.pageAt > DROP + 0.1) this.phase = 'spot';
      if (this.R && !this.landed && Game.time - this.pageAt > DROP * 0.7) { this.landed = true; this.sfx('paper', { gain: 0.45, rate: 1.1 }); this.sfxAny(['typewriter'], { gain: 0.35 }); }
    }
    keyDown(k) {
      const n = parseInt(k, 10);
      if (this.phase === 'intro') { if (k === 'Enter' || k === ' ') this.begin(); }
      else if (this.phase === 'drop' || this.phase === 'spot') { if (n >= 1 && n <= 3) this.tapLabel(n - 1); else if (k === 'a' || k === 'A') this.legit(); }
      else if (this.phase === 'ask') { if (n >= 1 && n <= 4) this.choose(n - 1); }
      else if (this.phase === 'fix') { if (k === 'Enter' || k === ' ') this.next(); }
      else if (this.phase === 'end') { if (k === 'Enter') this.endButtons()[0].go(); }
    }

    /* ---------------- drawing ---------------- */
    lay(docked) {
      const mode = docked ? 'd' : UI.touch() ? 't' : 'm', key = mode + '|' + Math.round(UI.text(40)) + '|' + this.bi;
      let L = this.lays.get(key);
      if (!L) { L = layPage(this.R, mode); this.lays.set(key, L); }
      return L;
    }
    sub() {
      if (!this.pages || this.phase === 'intro' || this.phase === 'end') return '';
      return `page ${this.bi + 1} of ${this.n} · ${this.stars} star${this.stars === 1 ? '' : 's'}`;
    }
    draw() {
      const docked = UI.docked();
      Z().cork();
      UI.hud('Fix the Zine', docked ? this.sub() : '');                     // the page carries it (the hud's line sits under the page)
      if (this.phase === 'intro') return this.drawIntro(docked);
      if (this.phase === 'end') return this.drawEnd(docked);
      const L = this.lay(docked);
      const s = E.outBack(prog(this.pageAt, DROP), 1.1);
      g.save(); g.translate(0, (1 - s) * -1100); g.rotate((1 - s) * -0.04);
      this.drawPage(L, docked);
      g.restore();
      if (docked) return this.drawDock();
      if (this.phase === 'drop' || this.phase === 'spot') this.drawSpot(L);
      else if (this.phase === 'ask') this.drawAsk(L);
      else this.drawFix(L);
    }

    /* the photocopied page: header, 3 examples with their stickers, the found circle, the fix */
    drawPage(L, docked) {
      const Zn = Z(), R = this.R, { X, Y, W, H, top, left } = L;
      Zn.page(X, Y, W, H, { seed: 400 + this.bi * 7 + (this.seed || 0) % 5, rot: 0, tape: 2, dark: 0.5 });
      Zn.staple(X - W / 2 + 60, top + 36, { rot: -0.7 });
      const t0 = this.pageAt + DROP * 0.6;
      const hy = top + (docked ? 62 : 58), hw = Zn.measure('DEVICE OF THE WEEK', L.headS);
      Zn.highlight(X - hw / 2 - 14, hy + 2, hw + 28, L.headS * 0.95, YEL, { t0: t0 + 0.35, dur: 0.35, seed: 11 + this.bi });
      Zn.type('DEVICE OF THE WEEK', X, hy, L.headS, { t0, cps: 45, align: 'center', seed: 300 + this.bi });
      Zn.type(`page ${this.bi + 1} of ${this.n} · ${this.stars} of ${this.of} stars so far`, X, hy + L.headS * 0.95, L.subS, { align: 'center', color: GREY, seed: 310 + this.bi });
      Zn.simileMark(X + W / 2 - 110, hy, 60, { seed: 9 });
      const spot = this.phase === 'spot' || this.phase === 'drop', live = this.phase === 'spot' && !docked;
      const fix = this.phase === 'fix', keys = UI.keys && spot && !docked;
      for (const row of L.rows) {
        const i = row.i, e = row.e, at = t0 + 0.15 + i * 0.2, S = row.st;
        // the words (a tap: the sticker's definition)
        let wordsHover = false, stHover = false;
        if (!docked && spot) {
          const hw_ = UI._hit('zn-w' + this.bi + '_' + i, row.words.x, row.words.y, row.words.w, row.words.h);
          wordsHover = hw_.hover; if (hw_.clicked) this.showDef(i);
        }
        if (live && !this.marks[i]) {
          const hs = UI._hit('zn-s' + this.bi + '_' + i, S.x, S.y, S.hw, S.hh);
          stHover = hs.hover; if (hs.clicked) this.tapLabel(i);
        }
        if (wordsHover || this.lastDef === i && spot && T - this.lastDefAt < 1.5) {
          g.save(); g.globalAlpha *= 0.35; g.fillStyle = '#9fd3ff'; g.fillRect(left - 12, row.words.y - row.words.h / 2 + 4, row.words.w + 4, row.words.h - 8); g.restore();
        }
        // the number
        const nx = left - (docked ? 64 : 58), ny = row.lines[0].y;
        inkCircle(nx, ny, docked ? 30 : 24, { w: 3.5, color: GREY, seed: 50 + i, passes: 1 });
        g.save(); g.font = fnt(docked ? 36 : 30, HAND); g.fillStyle = GREY; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(String(i + 1), nx, ny + 2); g.restore();
        row.lines.forEach((ln, k) => Zn.type(ln.s, left, ln.y, row.ts, { t0: at + k * 0.1, cps: 90, seed: 20 + i * 5 + k }));
        if (this.defs[i] && !docked && !fix) {
          g.save(); g.font = fnt(24, HAND); g.fillStyle = PARK; g.textAlign = 'center'; g.fillText('?', nx + 30, ny - 22); g.restore();
        }
        // the sticker (the wrong one peels off in the fix)
        const isBad = !R.legit && i === R.badIndex;
        const ss = pop(T, at + 0.35, 0.3, 2);
        if (fix && isBad) this.drawPeelAndFix(row, docked);
        else sticker(e.label, S.x, S.y - (stHover ? 4 : 0), row.ls, { rot: S.rot + (stHover ? -0.015 : 0), s: ss * (stHover ? 1.05 : 1), seed: 60 + i, faded: false });
        if (stHover) ellipse(S.x, S.y, S.w / 2 + 18, S.h / 2 + 14, { w: 3, color: 'rgba(217,53,44,0.45)', seed: 70 + i });
        if (keys) { g.save(); g.font = fnt(22, TYPE); g.fillStyle = GREY; g.textAlign = 'center'; g.fillText(`[${i + 1}]`, S.x, S.y + S.h / 2 + 22); g.restore(); }
        if (this.marks[i]) tick(S.x - S.w / 2 - 34, S.y, docked ? 1.5 : 1.1, { prog: prog(this.marks[i].at, 0.25), color: PARK, seed: 80 + i });
        if (this.found === i && !fix) ellipse(S.x, S.y, S.w / 2 + 24, S.h / 2 + 18, { w: docked ? 8 : 6, color: RED, seed: 90 + i, prog: prog(this.foundAt, 0.4) });
        if (fix && R.legit) tick(S.x - S.w / 2 - 34, S.y, docked ? 1.6 : 1.2, { prog: prog(this.fixAt + 0.3 + i * 0.15, 0.25), seed: 95 + i });
      }
      if (fix && R.legit) Zn.stamp('ALL CORRECT', X + W * 0.2, top + H - 90, { rot: -0.1, s: pop(T, this.fixAt + 0.8, 0.3) * (docked ? 0.9 : 0.7) });
    }
    /* Ms. Park's fix: the wrong sticker peels off, her highlighter swipes the right colour, she letters the right label */
    drawPeelAndFix(row, docked) {
      const S = row.st, e = row.e, t0 = this.fixAt + 0.25, id = this.rightId(), ls = row.ls;
      const p = prog(t0, 0.7);
      if (p < 1) {
        const lx = S.x - S.w / 2;
        g.save(); g.translate(lx, S.y + p * p * 420); g.rotate(p * 0.9); g.translate(S.w / 2, 0);
        sticker(e.label, 0, 0, ls, { rot: S.rot, alpha: 1 - p * p, seed: 60 + row.i });
        g.restore();
      }
      // the right label: a highlighter swipe in its colour, lettered on in marker
      const R2 = stickerSize(id, ls), w = R2.w, h = R2.h, x0 = row.st.x + S.w / 2 - w, sy = S.y;
      const t1 = t0 + 0.55, dur = 0.5, hp = prog(t1, dur);
      if (hp > 0) {
        Z().highlight(x0, sy, w, h * 0.95, id === 'none' ? '#cfcfc6' : Devices.colour(id), { t0: t1, dur, seed: 120 + this.bi, alpha: 0.85 });
        Z().doodle(id, x0 + ls * 0.85, sy, ls * 0.4, { t0: t1 + dur, dur: 0.3, seed: 125, w: Math.max(2, ls * 0.09) });
        hand(R2.t, x0 + ls * 1.6, sy + 2, ls, t1 + dur * 0.8, { align: 'left', font: MARKER, color: INK, seed: 130, dur: Math.max(0.3, R2.t.length * 0.04) });
        // the marker itself, riding the swipe
        if (hp < 1) {
          const mx = x0 + w * E.outQuad(hp), my = sy;
          g.save(); g.translate(mx + 40, my - 30); g.rotate(-0.7);
          paperRect(0, 0, 150, 34, id === 'none' ? '#cfcfc6' : Devices.colour(id), { seed: 7, depth: 1, boil: false });
          paperRect(-86, 0, 34, 30, INK, { seed: 8, depth: 0.5, boil: false });
          g.restore();
        }
      }
      if (T > t1 + dur) ellipse(x0 + w / 2, sy, w / 2 + 20, h / 2 + 14, { w: docked ? 6 : 4, color: PARK, seed: 140, prog: prog(t1 + dur + 0.4, 0.35) });
    }

    /* ---------------- the right-hand cards (desktop / landscape) ---------------- */
    panel() { const X = this.lay(false).panelX, W = 1890 - X, hs0 = UI.hudSlot(0); return { X, W, top: Math.max(118, hs0.y + hs0.r + 22) }; }
    card(x, top, w, h, seed) { Z().page(x + w / 2, top + h / 2, w, q10(h), { seed, rot: 0.004, dark: 0.25, tape: 1 }); }
    drawSpot(L) {
      const { X, W, top } = this.panel(), fs = UI.text(28), hs = UI.text(38), pad = 26;
      const touch = UI.touch(), body = touch && this.lastDef >= 0 ? [] : touch ? ['One sticker is wrong, or none is.', 'Tap the words: the label\'s definition (free).']
        : ['One sticker on this page is wrong. Or none is.', 'Tap the wrong label, or All correct.', 'Tap an example\'s words to read its label\'s definition (free).'];
      const bl = body.flatMap((s, i) => wrapF(s, W - pad * 2, fnt(fs, TYPE)).map(t => [t, i]));
      const h = 30 + hs * 1.3 + bl.length * fs * 1.3 + 24;
      this.card(X, top, W, h, 510);
      Z().type(`PAGE ${this.bi + 1} OF ${this.n}`, X + pad, top + 26 + hs * 0.6, hs, { t0: this.pageAt, cps: 40, seed: 511 });
      bl.forEach(([t, i], k) => Z().type(t, X + pad, top + 30 + hs * 1.3 + (k + 0.5) * fs * 1.3, fs, { color: i === body.length - 1 && body.length > 1 ? GREY : INK, seed: 520 + k }));
      const bh = UI.tap(88), by = top + q10(h) + 20 + bh / 2;
      if (Game.time - this.pageAt > 0.5 && UI.button('zn-legit', (UI.keys ? 'A  ' : '') + 'All correct ✓', X + W / 2, by, { w: Math.min(W - 30, 440), h: 88, color: YEL, text: INK })) this.legit();
      let y = by + bh / 2 + 26;
      const ns = UI.text(30);
      if (this.say && T - this.say.at < 7) {
        const sl = wrapF(this.say.text, W - 30, fnt(ns, HAND));
        sl.forEach((t, k) => hand(t, X + 10, y + (k + 0.5) * ns * 1.2, ns, this.say.at + k * 0.2, { align: 'left', color: this.misses >= MAX_MISSES ? PARK : RED, seed: 530 + k }));
        y += sl.length * ns * 1.2 + 16;
      }
      if (this.lastDef >= 0 && y < 1000) this.defCard(X, y, W, 1070 - y);
    }
    /* the definition of the last example tapped: a small taped slip */
    defCard(X, y, W, maxH) {
      const D = this.defText(this.lastDef);
      for (const k of [1, 0.85, 0.72, 0.6]) {
        const hs = UI.text(28) * k, fs = UI.text(26) * k, ds = UI.text(22) * k;
        const sl = wrapF(D.short, W - 50, fnt(fs, HAND)), dl = wrapF(`"${D.def}"`, W - 50, fnt(ds, TYPE));
        const h = 22 + hs * 1.3 + sl.length * fs * 1.2 + dl.length * ds * 1.25 + 18;
        if (h > maxH && k > 0.6) continue;
        const at = this.lastDefAt;
        paperRect(X + W / 2, y + h / 2, Math.round(W), q10(h), '#fffdf2', { seed: 540 + this.lastDef, depth: 0.8, boil: false, rot: -0.006 });
        tape(X + W / 2, y + 2, 120, 0.04, Z().C.tape, 541);
        const S = this.R.examples[this.lastDef];
        Z().highlight(X + 20, y + 20 + hs * 0.6, Z().measure(`${this.lastDef + 1}. ${D.head}`, hs) + 10, hs * 0.9, S.label === 'none' ? '#cfcfc6' : Devices.colour(S.label), { prog: 1, seed: 542 });
        Z().type(`${this.lastDef + 1}. ${D.head}`, X + 24, y + 20 + hs * 0.6, hs, { t0: at, cps: 60, seed: 543 });
        let yy = y + 22 + hs * 1.3;
        sl.forEach((t, j) => hand(t, X + 24, yy + (j + 0.5) * fs * 1.2, fs, at + 0.1, { align: 'left', color: PARK, seed: 544 + j })); yy += sl.length * fs * 1.2;
        dl.forEach((t, j) => Z().type(t, X + 24, yy + (j + 0.5) * ds * 1.25, ds, { color: GREY, seed: 548 + j }));
        return;
      }
    }
    askLayout() {
      const { X, W, top } = this.panel(), R = this.R, q = R.ask, pad = 26;
      const intro = this.revealed ? (R.legit ? 'Ms. Park: every sticker was right.' : 'Ms. Park circled the wrong one.') : R.legit ? 'Right: all correct!' : 'Got it: that sticker is wrong.';
      const last = this.picks[this.picks.length - 1], why = last != null && !this.isRight(last) ? q.why[last] : '';
      let C;
      for (const k of [1, 0.9, 0.8, 0.7, 0.6]) {
        const is = UI.text(32) * k, qs = UI.text(32) * k, os = UI.text(R.legit ? 26 : 30) * k, ws = UI.text(28) * k;
        const iL = wrapF(intro, W - pad * 2, fnt(is, HAND)), qL = wrapF(q.prompt, W - pad * 2, fnt(qs, TYPE));
        const opts = q.options.map((o, i) => {
          if (!R.legit) { const S = stickerSize(o.id, os); return { i, h: Math.max(UI.tap(86) + 4, S.h + 22), S }; }
          const lines = wrapF(o.label, W - pad * 2 - 70, fnt(os, TYPE));
          return { i, lines, h: Math.max(UI.tap(86) + 4, lines.length * os * 1.25 + 26) };
        });
        const whyL = why ? wrapF(why, W - pad * 2, fnt(ws, HAND)) : [];
        const head = 24 + iL.length * is * 1.2 + qL.length * qs * 1.25 + 14;
        const h = head + opts.reduce((a, o) => a + o.h, 0) + 16 + (whyL.length ? whyL.length * ws * 1.2 + 16 : 0);
        C = { X, W, pad, top, intro, iL, qL, opts, whyL, is, qs, os, ws, head, h, last };
        if (top + h <= 1070) break;
      }
      return C;
    }
    drawAsk() {
      const R = this.R, q = R.ask, C = this.askLayout(), { X, W, pad, top, iL, qL, opts, whyL, is, qs, os, ws, head, h } = C, t0 = this.askAt;
      if (T < t0) return;
      this.card(X, top, W, h, 560);
      iL.forEach((l, i) => hand(l, X + pad, top + 22 + (i + 0.5) * is * 1.2, is, t0, { align: 'left', color: this.revealed ? PARK : '#2f8f3a', seed: 561 + i }));
      const qy = top + 22 + iL.length * is * 1.2;
      qL.forEach((l, i) => Z().type(l, X + pad, qy + (i + 0.5) * qs * 1.25, qs, { t0: t0 + 0.15, cps: 70, seed: 565 + i }));
      let y = top + head;
      opts.forEach(o => {
        const i = o.i, cy = y + o.h / 2, crossed = this.picks.includes(i);
        g.save(); g.fillStyle = 'rgba(20,18,16,0.15)'; g.fillRect(X + 14, y + o.h - 1, W - 28, 2); g.restore();
        const hit = !crossed ? UI._hit('zn-opt' + this.bi + '_' + i, X + W / 2, cy, W - 20, o.h) : { hover: false, clicked: false };
        if (hit.hover) { g.save(); g.globalAlpha *= 0.35; g.fillStyle = YEL; g.fillRect(X + 12, y + 5, W - 24, o.h - 10); g.restore(); }
        const kx = X + pad + 20;
        g.save(); g.lineWidth = 3; g.strokeStyle = INK; g.strokeRect(kx - 19, cy - 19, 38, 38);
        g.font = fnt(24, TYPE); g.fillStyle = INK; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(String(i + 1), kx, cy + 1); g.restore();
        let x1;
        if (!R.legit) {
          const sx = kx + 40 + o.S.w / 2;
          sticker(q.options[i].id, sx, cy, os, { rot: (i - 1) * 0.02, s: pop(T, t0 + 0.2 + i * 0.1, 0.3, 2), seed: 570 + i, faded: crossed });
          x1 = sx + o.S.w / 2;
        } else {
          o.lines.forEach((l, k) => Z().type(l, kx + 40, cy + (k - (o.lines.length - 1) / 2) * os * 1.25, os, { color: crossed ? GREY : INK, seed: 575 + i * 3 + k }));
          x1 = kx + 40 + Math.max(...o.lines.map(l => Z().measure(l, os)));
        }
        if (crossed) ink([[kx + 30, cy + 10], [x1 + 10, cy - 10]], { w: 5, color: RED, seed: 580 + i, prog: i === C.last ? prog(this.pickedAt, 0.2) : 1, passes: 1, step: 12 });
        if (hit.clicked) this.choose(i);
        y += o.h;
      });
      y += 16;
      whyL.forEach((l, k) => hand(l, X + pad, y + (k + 0.5) * ws * 1.2, ws, this.pickedAt + 0.2, { align: 'left', color: RED, seed: 590 + k }));
    }
    fixLines() {
      const R = this.R, res = this.results[this.bi] || { stars: 0, spotStar: 0, askStar: 0 }, out = [];
      if (R.legit) {
        out.push(['head', res.spotStar ? 'ALL CORRECT!' : 'IT WAS ALL CORRECT']);
        out.push(['type', R.ask.prompt]);
        out.push(['right', R.ask.options[R.ask.answerIndex].label]);
      } else {
        const ex = R.examples[R.badIndex];
        out.push(['head', "MS. PARK'S HIGHLIGHTER"]);
        out.push(['type', `"${ex.text}"`]);
        out.push(['swap', LABEL(ex.label) + '  →  ' + LABEL(this.rightId())]);
      }
      out.push(['note', R.fix || '']);
      out.push(['stars', `+${res.stars} star${res.stars === 1 ? '' : 's'} (${R.legit ? 'all correct' : 'sticker'} ${res.spotStar}, ${R.legit ? 'question' : 'label'} ${res.askStar})`]);
      return out;
    }
    fixCard() {
      const key = this.bi + '|' + Math.round(UI.text(28));
      if (this._fc && this._fc.key === key) return this._fc;
      const { X, W, top } = this.panel(), F = this.fixLines(), bh = UI.tap(88), pad = 26;
      let C;
      for (const k of [1, 0.9, 0.8, 0.7, 0.62]) {
        const S = { head: [UI.text(34) * k, TYPE], type: [UI.text(28) * k, TYPE], swap: [UI.text(30) * k, MARKER], right: [UI.text(30) * k, HAND], note: [UI.text(32) * k, HAND], stars: [UI.text(30) * k, HAND] };
        const lh = kk => S[kk][0] * 1.28;
        const rows = F.flatMap(([kk, t]) => wrapF(t, W - pad * 2, fnt(S[kk][0], S[kk][1])).map(l => [kk, l]));
        const h = rows.reduce((a, [kk]) => a + lh(kk), 0) + 30 + 3 * 8;
        C = { key, X, W, pad, rows, h, S, lh, bh, top };
        if (top + h + 22 + bh + 12 <= 1080) break;
      }
      return (this._fc = C);
    }
    drawFix() {
      const C = this.fixCard(), { X, W, pad, rows, h, S, lh, bh, top } = C;
      this.card(X, top, W, h, 600);
      let y = top + 18, tt = this.fixAt + 0.2, prev = null;
      rows.forEach(([k, l], j) => {
        if (prev && prev !== k) y += 8; prev = k;
        const s = S[k][0], cy = y + lh(k) / 2;
        if (k === 'head') Z().type(l, X + pad, cy, s, { t0: tt, cps: 50, color: RED, seed: 601 + j });
        else if (k === 'type') Z().type(l, X + pad, cy, s, { color: INK, seed: 610 + j });
        else if (k === 'swap') hand(l, X + pad, cy, s, tt + 0.6, { align: 'left', font: MARKER, color: INK, seed: 620 + j });
        else if (k === 'right') hand(l, X + pad, cy, s, tt + 0.3, { align: 'left', color: '#2f8f3a', seed: 625 + j });
        else if (k === 'note') hand(l, X + pad, cy, s, tt + 1.1 + j * 0.05, { align: 'left', color: PARK, seed: 630 + j });
        else hand(l, X + pad, cy, s, tt + 1.4, { align: 'left', color: RED, seed: 640 + j });
        y += lh(k);
      });
      const label = this.bi < this.n - 1 ? 'Next page ▶' : 'See my stars ▶';
      if (Game.time - this.fixAt > NEXT_GUARD && UI.button('zn-next', label, X + W / 2, top + q10(h) + 22 + bh / 2, { w: Math.min(W - 30, 420), h: 88 })) this.next();
    }

    /* ---------------- portrait: the page is the picture; everything else in the dock ---------------- */
    drawDock() {
      const R = this.R, q = R.ask, id = this.attempt + '_' + this.bi;
      Dock.head(`Page ${this.bi + 1} of ${this.n}`, `${this.stars} of ${this.of} stars`);
      const opts = R.examples.map((e, i) => `"${e.text}"  →  ${LABEL(e.label)}`);
      if (this.phase === 'drop' || this.phase === 'spot') {
        Dock.text('zn-how', ['One sticker is wrong, or none is. Tap the wrong label, or All correct.'], { cls: 'note' });
        if (this.phase === 'spot') {
          Dock.choices('zn-st' + id, { prompt: 'Which label is wrong?', options: opts, state: R.examples.map((e, i) => (this.marks[i] ? 'faded' : '')),
            notes: R.examples.map((e, i) => (this.marks[i] ? 'right label' : '')), onChoose: i => this.tapLabel(i) });
          Dock.buttons('zn-legit' + id, [{ label: 'All correct ✓', wide: true, onClick: () => this.legit() }]);
          if (this.say) Dock.text('zn-say' + id + '_' + this.misses, [this.say.text], { cls: 'wrong', reveal: true });
          this.dockDefs(id);
        }
        return;
      }
      if (this.phase === 'ask') {
        const f = this.found >= 0 ? (this.revealed ? 'Ms. Park circled: ' : 'You found it: ') + opts[this.found] : (this.revealed ? 'Ms. Park: every sticker was right.' : 'Right: all correct!');
        Dock.text('zn-found' + id, [f], { cls: this.revealed ? 'note' : 'right' });
        Dock.choices('zn-ask' + id, { prompt: q.prompt, options: q.options.map(o => (R.legit ? o.label : LABEL(o.id))), state: q.options.map((o, i) => (this.picks.includes(i) ? 'wrong' : '')),
          notes: q.options.map((o, i) => (this.picks.includes(i) ? q.why[i] : '')), onChoose: i => this.choose(i) });
        return;
      }
      // fix
      const F = this.fixLines(), right = this.picked != null ? this.picked : q.answerIndex;
      Dock.choices('zn-ask' + id, { prompt: q.prompt, options: q.options.map(o => (R.legit ? o.label : LABEL(o.id))),
        state: q.options.map((o, i) => (i === right ? 'right' : this.picks.includes(i) ? 'wrong' : 'faded')), locked: true });
      Dock.heading('zn-fix' + id, F[0][1]);
      if (!R.legit) Dock.list('zn-sw' + id, [{ badge: String(R.badIndex + 1), color: RED, lines: [F[1][1], F[2][1]] }]);
      Dock.text('zn-note' + id, [R.fix || ''], { cls: 'note' });
      Dock.text('zn-stars' + id, [F[F.length - 1][1]], { cls: 'right', reveal: true });
      Dock.button({ id: 'next', glyph: this.bi < this.n - 1 ? 'Next page ▶' : 'See my stars ▶', label: 'next', aria: 'next page', go: true, onClick: () => this.next() });
    }
    dockDefs(id) {
      const R = this.R;
      Dock.text('zn-defq' + id, ['What does a label mean? (free)'], { cls: 'small' });
      Dock.buttons('zn-defb' + id, R.examples.map((e, i) => ({ label: `${i + 1}: ${LABEL(e.label)}?`, onClick: () => this.showDef(i) })));
      if (this.lastDef >= 0) { const D = this.defText(this.lastDef); Dock.text('zn-deft' + id + '_' + this.lastDef, [`${D.head}: ${D.short}`, `"${D.def}"`], { cls: 'note', reveal: true }); }
    }

    drawIntro(docked) {
      const Zn = Z(), t0 = this.enteredAt, n = this.n, pass = this.passStars, of = this.of;
      const lines = ['Nia pasted the stickers on this week\'s zine. Some are wrong.', `${n} pages, 3 examples each. All but one page has ONE wrong label.`,
        'Tap the wrong sticker (or All correct), then pick the right label.', `${pass} of ${of} stars for the stamp.`];
      const rise = E.outBack(P(T, t0 + 0.2, t0 + 0.7), 1.4);
      if (docked) {
        Zn.page(1080, 540, 1500, 900, { seed: 77, rot: 0.01, tape: 2, dark: 0.5 });
        Zn.highlight(1080 - 330, 300, 660, 100, YEL, { t0: t0 + 0.5, seed: 78 });
        Zn.type('FIX THE ZINE', 1080, 300, 110, { t0: t0 + 0.1, cps: 30, align: 'center', seed: 79 });
        sticker('simile', 900, 560, 70, { rot: -0.05, s: pop(T, t0 + 0.6), seed: 80 });
        sticker('none', 1250, 720, 62, { rot: 0.04, s: pop(T, t0 + 0.8), seed: 81 });
        Puppet.draw('park', 250, 1080 + (1 - rise) * 500, 760, { facing: 1, t: T });
        Dock.heading('zn-intro', 'Fix the Zine', { cls: 'big' });
        Dock.text('zn-intro', lines);
        Dock.button({ id: 'go', glyph: 'Start ▶', label: 'start', aria: 'Start fixing the zine', go: true, color: 'red', onClick: () => this.begin() });
        return;
      }
      Puppet.draw('park', 300, 1080 + (1 - rise) * 500, 720, { facing: 1, t: T });
      Zn.page(1160, 590, 1340, 900, { seed: 77, rot: 0.006, tape: 2, dark: 0.5 });
      Zn.staple(560, 190, { rot: -0.6 });
      Zn.highlight(1160 - 300, 250, 600, 90, YEL, { t0: t0 + 0.5, seed: 78 });
      Zn.type('FIX THE ZINE', 1160, 250, 96, { t0: t0 + 0.1, cps: 30, align: 'center', seed: 79 });
      Zn.type('device of the week · the fix-it issue', 1160, 325, 30, { align: 'center', color: GREY, seed: 82 });
      const fs = UI.text(32);
      let y = 420;
      lines.forEach((l, i) => wrapF(l, 1140, fnt(fs, TYPE)).forEach(t => { Zn.type(t, 1160, y, fs, { t0: t0 + 0.4 + i * 0.25, cps: 80, align: 'center', seed: 83 + i, color: i === 3 ? RED : INK }); y += fs * 1.45; }));
      y += 20;
      sticker('simile', 930, y + 30, UI.text(34), { rot: -0.05, s: pop(T, t0 + 1.2), seed: 80 });
      sticker('none', 1390, y + 30, UI.text(34), { rot: 0.04, s: pop(T, t0 + 1.35), seed: 81 });
      const by = Math.min(960, y + 160);
      if (this.t > 0.8 && UI.button('zn-start', 'Start ▶', 1160, by, { w: 400, h: 104 })) this.begin();
      if (this.t > 1.2) hand(UI.hint('(or press Enter)', ''), 1160, by + 76, UI.text(28), 0, { dur: 0.01, color: GREY });
    }
    drawEnd(docked) {
      const Zn = Z(), s = this.stars, of = this.of, btns = this.endButtons();
      const rows = this.pages.map((pg, i) => { const r = this.results[i] || { stars: 0 }; return { r, text: `${i + 1}. ${SHORT[pg.cheat] || pg.cheat}` }; });
      const word = this.passed ? 'PUBLISHED' : 'TRY AGAIN';
      if (docked) {
        Zn.page(960, 540, 1500, 900, { seed: 91, rot: -0.008, tape: 2, dark: 0.5 });
        Zn.type(`${s} / ${of}`, 960, 400, 200, { t0: this.endAt + 0.1, cps: 20, align: 'center', seed: 92 });
        Zn.stamp(word, 960, 720, { rot: -0.08, s: pop(T, this.endAt + 0.5, 0.3) * 1.6 });
        Dock.heading('zn-end', `Your stars: ${s} / ${of}`, { cls: 'big' });
        Dock.text('zn-end', [this.passed ? 'Every label fixed! Collect your stamp.' : `Not yet: ${this.passStars} stars for the stamp. New pages?`]);
        Dock.list('zn-end', rows.map((x, i) => ({ badge: String(i + 1), color: x.r.stars === 2 ? '#2f8f3a' : RED, lines: [x.text.replace(/^\d+\. /, ''), `${x.r.stars} of 2 stars`] })));
        Dock.text('zn-code', [`Quiz code ${this.seed}`], { cls: 'small' });
        Dock.buttons('zn-end', btns.map((b, i) => ({ label: b.label, wide: i === 0, onClick: b.go })), { bottom: true });
        return;
      }
      Zn.page(960, 600, 1500, 900, { seed: 91, rot: -0.004, tape: 2, dark: 0.5 });
      Zn.type(`${s} / ${of}`, 700, 250, 120, { t0: this.endAt + 0.1, cps: 20, align: 'center', seed: 92 });
      Zn.stamp(word, 1300, 250, { rot: -0.08, s: pop(T, this.endAt + 0.5, 0.3) * 1.0 });
      const fs = UI.text(32), rh = Math.max(64, fs * 1.45), bh = UI.tap(100);
      const top = Math.min(380, 1080 - 40 - bh - 90 - rows.length * rh);
      rows.forEach((x, i) => {
        const y = top + i * rh;
        Zn.type(x.text, 340, y, fs, { t0: this.endAt + 0.3 + i * 0.1, cps: 90, seed: 93 + i });
        hand(`${x.r.stars} of 2`, 1560, y, fs * 1.2, this.endAt + 0.4 + i * 0.1, { align: 'right', color: x.r.stars === 2 ? '#2f8f3a' : RED, seed: 100 + i });
        if (x.r.stars === 2) tick(1610, y, 0.9, { prog: prog(this.endAt + 0.5 + i * 0.1, 0.25), seed: 110 + i });
      });
      const py = top + rows.length * rh + 10, by = Math.min(1080 - 30 - bh / 2, py + 50 + bh / 2);
      hand(this.passed ? 'Every label fixed! Collect your stamp.' : `Not yet: ${this.passStars} stars for the stamp.`, 960, py, UI.text(36), this.endAt + 0.8, { color: this.passed ? PARK : RED });
      btns.forEach((b, i) => { if (this.t > 0.2 && UI.button('zn-end' + i, b.label, 700 + i * 520, by, { w: 470, h: 100, color: i === 0 ? RED : YEL, text: i === 0 ? '#fffefa' : INK })) b.go(); });
      Zn.type(`quiz code ${this.seed}`, 1690, by + bh / 2 + 18 > 1068 ? 1060 : by + bh / 2 + 18, UI.text(20), { align: 'right', color: GREY, seed: 120 });
    }
  }
  FixZine.STEP = STEP; FixZine.layPage = layPage; FixZine.sticker = sticker;
  Object.defineProperty(FixZine, 'solution', { get() { const s = Game.current(); return s instanceof FixZine ? s.solution : null; } });
  /* the live page's canvas tap rects (tests tap them): stickers + words, centred {i, kind, x, y, w, h} */
  FixZine.targets = () => {
    const s = Game.current();
    if (!(s instanceof FixZine) || !s.R) return [];
    const L = s.lay(UI.docked());
    return L.rows.flatMap(r => [{ i: r.i, kind: 'sticker', x: r.st.x, y: r.st.y, w: r.st.hw, h: r.st.hh }, { i: r.i, kind: 'words', ...r.words }]);
  };
  Levels.zine = FixZine;
})();
